import { observer } from 'mobx-react-lite'
import { useTheme } from '@mui/material/styles'

import {
  useCategoryStore,
  useGlobalCopyStore,
  INCOME_ACCOUNT_TYPES,
  PAYMENT_ACCOUNT_TYPES,
  SPENDING_ACCOUNT_TYPES,
} from '../../common'
import { AccountType, MonthlyCategoryTotals, TrendsCopy } from '../../core'

import { useAccountUiStore } from '../../accounts/hooks/useUiStore'
import { LineChart } from '../../common/components/charts/LineChart'
import type { LineChartDataset, LineChartLabel } from '../../common/components/charts/LineChart'

import { getIncomeTotals, getPaymentTotals, getSpendingTotals } from '../utils/SpendingData'

const getDatasets = (
  totals: MonthlyCategoryTotals[],
  accountType: AccountType,
): LineChartDataset[] => {
  const incomeData = INCOME_ACCOUNT_TYPES.includes(accountType) ? getIncomeTotals(totals) : []
  const paymentData = PAYMENT_ACCOUNT_TYPES.includes(accountType) ? getPaymentTotals(totals) : []
  const spendingData = SPENDING_ACCOUNT_TYPES.includes(accountType) ? getSpendingTotals(totals) : []

  // There should always be 1-2 datasets returned. No account type will have all three categories.
  return [incomeData, paymentData, spendingData].filter((data) => data.length > 0)
}

const getLabels = (datasets: LineChartDataset[], accountType: AccountType, copy: TrendsCopy) => {
  const [dataA, dataB] = datasets
  const credits = dataA
  const debits = dataB ?? dataA
  const labels: LineChartLabel[] = []

  if (credits && debits && INCOME_ACCOUNT_TYPES.includes(accountType)) {
    const lastIncome = credits[credits.length - 1].y
    labels.push({ label: copy.income_label, amount: lastIncome })
  } else if (PAYMENT_ACCOUNT_TYPES.includes(accountType)) {
    const lastPayment = credits[credits.length - 1].y
    labels.push({ label: copy.payments_label, amount: lastPayment })
  }

  if (debits && SPENDING_ACCOUNT_TYPES.includes(accountType)) {
    const lastSpending = debits[debits.length - 1].y
    labels.push({ label: copy.spending_label, amount: lastSpending })
  }

  return labels
}

const TrendsChart = () => {
  const theme = useTheme()
  const { selectedAccount } = useAccountUiStore()
  const { monthlyCategoryTotals } = useCategoryStore()
  const { trends: trendsCopy } = useGlobalCopyStore()

  const colors = [theme.palette.chart?.chart1, theme.palette.chart?.chart2]
  const datasets = getDatasets(monthlyCategoryTotals, selectedAccount.account_type)
  const labels = getLabels(datasets, selectedAccount.account_type, trendsCopy)
  const isLoan = [AccountType.LOAN, AccountType.MORTGAGE].includes(selectedAccount.account_type)

  return (
    <div data-testid="TrendsChart">
      <LineChart
        colors={colors}
        datasets={datasets}
        height={185}
        labels={labels}
        showAverage={isLoan}
        showAxisHighlight={true}
        showBars={isLoan}
        showLegend={true}
        showTooltip={true}
        showXAxis={true}
        showXAxisTicks={true}
        showYAxis={true}
        showYAxisTicks={true}
        valueFormatterString="0,0"
      />
    </div>
  )
}

export default observer(TrendsChart)
